import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, Car, Route, Calendar, FileText, LogOut, TrendingUp, Users, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';

function AdminDashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background flex">
      <div className="w-64 bg-primary text-white p-6">
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
            alt="Hasaka Tours & Travels"
            className="h-16 w-auto mb-3 brightness-0 invert"
          />
          <h2 className="font-manrope font-bold text-xl">Admin Panel</h2>
        </div>
        <nav className="space-y-2">
          <Link
            to="/admin"
            className="flex items-center gap-3 px-4 py-3 rounded-sm bg-primary-900 text-white font-medium"
            data-testid="admin-dashboard-nav"
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link
            to="/admin/vehicles"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
            data-testid="vehicles-nav"
          >
            <Car className="w-5 h-5" />
            Vehicles
          </Link>
          <Link
            to="/admin/routes"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
            data-testid="routes-nav"
          >
            <Route className="w-5 h-5" />
            Routes
          </Link>
          <Link
            to="/admin/schedules"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
            data-testid="schedules-nav"
          >
            <Calendar className="w-5 h-5" />
            Schedules
          </Link>
          <Link
            to="/admin/bookings"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
            data-testid="bookings-nav"
          >
            <FileText className="w-5 h-5" />
            Bookings
          </Link>
        </nav>
        <div className="mt-auto pt-12">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="w-full mb-3 border-white text-white hover:bg-white hover:text-primary rounded-sm"
          >
            User Dashboard
          </Button>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full text-white hover:bg-primary-900 rounded-sm"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex-1 p-12">
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-4xl text-primary mb-2" data-testid="admin-dashboard-heading">
            Dashboard Overview
          </h1>
          <p className="text-slate-600 font-public-sans text-lg">Monitor your business performance</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-text-secondary font-public-sans">Loading analytics...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white rounded-sm p-8 border-l-4 border-l-secondary shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide font-public-sans mb-1">Total Bookings</p>
                  <p className="font-manrope text-4xl font-bold text-primary" data-testid="total-bookings">
                    {analytics?.total_bookings || 0}
                  </p>
                </div>
                <FileText className="w-12 h-12 text-secondary" />
              </div>
            </div>

            <div className="bg-white rounded-sm p-8 border-l-4 border-l-green-500 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide font-public-sans mb-1">Confirmed Bookings</p>
                  <p className="font-manrope text-4xl font-bold text-primary" data-testid="confirmed-bookings">
                    {analytics?.confirmed_bookings || 0}
                  </p>
                </div>
                <Users className="w-12 h-12 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-sm p-8 border-l-4 border-l-blue-500 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide font-public-sans mb-1">Total Revenue</p>
                  <p className="font-manrope text-4xl font-bold text-primary" data-testid="total-revenue">
                    ${analytics?.total_revenue?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <DollarSign className="w-12 h-12 text-blue-500" />
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 grid grid-cols-2 gap-6">
          <div className="bg-white rounded-sm p-8 shadow-sm border border-slate-200">
            <h3 className="font-manrope font-bold text-2xl text-primary mb-6">Quick Actions</h3>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/admin/vehicles')}
                className="w-full justify-start bg-slate-100 text-primary hover:bg-slate-200 rounded-sm px-6 py-4"
              >
                <Car className="w-5 h-5 mr-3" />
                Manage Vehicles
              </Button>
              <Button
                onClick={() => navigate('/admin/routes')}
                className="w-full justify-start bg-slate-100 text-primary hover:bg-slate-200 rounded-sm px-6 py-4"
              >
                <Route className="w-5 h-5 mr-3" />
                Manage Routes
              </Button>
              <Button
                onClick={() => navigate('/admin/schedules')}
                className="w-full justify-start bg-slate-100 text-primary hover:bg-slate-200 rounded-sm px-6 py-4"
              >
                <Calendar className="w-5 h-5 mr-3" />
                Manage Schedules
              </Button>
              <Button
                onClick={() => navigate('/admin/bookings')}
                className="w-full justify-start bg-slate-100 text-primary hover:bg-slate-200 rounded-sm px-6 py-4"
              >
                <FileText className="w-5 h-5 mr-3" />
                View All Bookings
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-sm p-8 shadow-sm border border-slate-200">
            <h3 className="font-manrope font-bold text-2xl text-primary mb-6">System Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-600 font-public-sans">System Status</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-sm text-sm font-semibold">Online</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600 font-public-sans">Payment Gateway</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-sm text-sm font-semibold">Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600 font-public-sans">Database</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-sm text-sm font-semibold">Connected</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;